import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const PropertyContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const PropertyProvider = ({ children }) => {
  const [properties, setProperties] = useState([]);
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    transaction_type: '',
    property_type: '',
    neighborhood: '',
    price_min: '',
    price_max: '',
    bedrooms_min: '',
    bathrooms_min: '',
    sort_by: 'newest',
  });

  const fetchProperties = useCallback(async (customFilters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const activeFilters = { ...filters, ...customFilters };
      
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          params.append(key, value);
        }
      });
      
      const response = await axios.get(`${API_URL}/properties?${params.toString()}`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchFeaturedProperties = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/properties/featured`);
      setFeaturedProperties(response.data);
    } catch (error) {
      console.error('Error fetching featured properties:', error);
    }
  }, []);

  const fetchAmenities = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/amenities`);
      setAmenities(response.data);
    } catch (error) {
      console.error('Error fetching amenities:', error);
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/tags`);
      setTags(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  }, []);

  const getProperty = useCallback(async (id) => {
    try {
      const response = await axios.get(`${API_URL}/properties/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching property:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchFeaturedProperties();
    fetchAmenities();
    fetchTags();
  }, [fetchFeaturedProperties, fetchAmenities, fetchTags]);

  const updateFilters = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const clearFilters = () => {
    setFilters({
      transaction_type: '',
      property_type: '',
      neighborhood: '',
      price_min: '',
      price_max: '',
      bedrooms_min: '',
      bathrooms_min: '',
      sort_by: 'newest',
    });
  };

  return (
    <PropertyContext.Provider
      value={{
        properties,
        featuredProperties,
        amenities,
        tags,
        loading,
        filters,
        fetchProperties,
        fetchFeaturedProperties,
        getProperty,
        updateFilters,
        clearFilters,
      }}
    >
      {children}
    </PropertyContext.Provider>
  );
};

export const useProperties = () => {
  const context = useContext(PropertyContext);
  if (!context) {
    throw new Error('useProperties must be used within a PropertyProvider');
  }
  return context;
};
